import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BudgetConflictComparator } from '@/components/budget/BudgetConflictComparator';
import { LoadingOverlay } from '@/components/ui/Loading';
import { useUpdateBudget } from '@/hooks/useBudgets';
import { ApiException } from '@/lib/api';
import { BudgetService } from '@/services/BudgetService';
import { toast } from 'sonner';
import type { Budget, ConflictError, UpdateBudgetRequest } from '@/types';

interface BudgetConflictState {
  serverBudget: Budget;
  clientRequest: UpdateBudgetRequest;
  message: string;
}

function conflictStorageKey(budgetId: string): string {
  return `budget-conflict:${budgetId}`;
}

function buildRequestFromServer(serverBudget: Budget): UpdateBudgetRequest {
  return {
    name: serverBudget.name,
    type: serverBudget.type,
    parentId: serverBudget.parentId ?? undefined,
    currency: serverBudget.currency,
    amount: serverBudget.amount,
    startDate: serverBudget.startDate,
    endDate: serverBudget.endDate,
    rolloverEnabled: serverBudget.rolloverEnabled,
    rolloverAmount: serverBudget.rolloverAmount ?? undefined,
    version: serverBudget.version,
  };
}

export function BudgetConflictPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const updateBudget = useUpdateBudget();
  const [state, setState] = React.useState<BudgetConflictState | null>(null);

  React.useEffect(() => {
    if (!id) return;

    const fromNavigation = location.state as BudgetConflictState | null;
    if (fromNavigation?.serverBudget && fromNavigation?.clientRequest) {
      setState(fromNavigation);
      sessionStorage.setItem(conflictStorageKey(id), JSON.stringify(fromNavigation));
      return;
    }

    const raw = sessionStorage.getItem(conflictStorageKey(id));
    if (!raw) {
      toast.error('No pending conflict to resolve.');
      navigate(`/budgets/${id}/edit`, { replace: true });
      return;
    }

    try {
      const parsed = JSON.parse(raw) as BudgetConflictState;
      if (!parsed?.serverBudget || !parsed?.clientRequest) {
        throw new Error('Invalid conflict state');
      }
      setState(parsed);
    } catch {
      toast.error('Conflict data is invalid. Please edit the budget again.');
      navigate(`/budgets/${id}/edit`, { replace: true });
    }
  }, [id, location.state, navigate]);

  React.useEffect(() => {
    if (!id || !state) return;
    sessionStorage.setItem(conflictStorageKey(id), JSON.stringify(state));
  }, [id, state]);

  if (!id || !state) {
    return <LoadingOverlay message="Loading conflict resolver..." />;
  }

  const handleMyVersionChange = (next: UpdateBudgetRequest) => {
    setState((prev) => (prev ? { ...prev, clientRequest: next } : prev));
  };

  const handleCopyServerToMine = () => {
    setState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        clientRequest: buildRequestFromServer(prev.serverBudget),
      };
    });
  };

  const handleUseServerVersion = () => {
    sessionStorage.removeItem(conflictStorageKey(id));
    navigate(`/budgets/${id}/edit`, {
      replace: true,
      state: { forceReload: Date.now() },
    });
    toast.success('Using latest server version.');
  };

  const handleApplyMyVersion = async () => {
    const request: UpdateBudgetRequest = {
      ...state.clientRequest,
      version: state.serverBudget.version,
    };

    try {
      await updateBudget.mutateAsync({ id, request });
      sessionStorage.removeItem(conflictStorageKey(id));
      navigate(`/budgets/${id}`);
    } catch (error) {
      if (error instanceof ApiException && error.isConflict) {
        const conflictError = error.error as Partial<ConflictError>;
        const latest = await BudgetService.getById(id);
        if (!latest) {
          toast.error('Could not refresh latest server version. Please try again.');
          return;
        }

        setState((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            serverBudget: latest,
            message:
              conflictError.message ??
              'Conflict happened again. Review differences and resolve.',
          };
        });
      }
    }
  };

  return (
    <BudgetConflictComparator
      message={state.message}
      serverBudget={state.serverBudget}
      myVersion={state.clientRequest}
      onMyVersionChange={handleMyVersionChange}
      onCopyServerToMine={handleCopyServerToMine}
      onUseServerVersion={handleUseServerVersion}
      onApplyMyVersion={handleApplyMyVersion}
      isApplying={updateBudget.isPending}
    />
  );
}